import type { UserId } from "@inrev/common";
import { useAdminCheckCompanyByName, useAdminSubmitSuretyAccount } from "@portal/api/account/admin";
import {
	useAdminFetchAgencyPreviews,
	useAdminFetchAgentPreviewsForAgency,
} from "@portal/api/agency/admin";
import { useRequireActiveAgentFollower } from "@portal/api/followers";
import { createFormResolver } from "@portal/components/multiform/utils/formResolver";
import { useIsCommercialSuretyEnabled } from "@portal/hooks/useIsCommercialSuretyEnabled";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import type { z } from "zod";
import type { BondFormData } from "../../../../../types";
import {
	type AccountSubmissionAnalytics,
	useAccountSubmissionAnalytics,
} from "../../../../agent/account/create/useAccountSubmissionAnalytics";
import {
	BondRequestCreateContext,
	type PrincipalCompanyOption,
} from "../../../../agent/request/create/BondRequestCreateView";
import { transformBondFormForSubmission } from "../../../../agent/request/create/schema";
import { AdminDuplicateCompaniesModal } from "../../../shared/submission/AdminDuplicateCompaniesModal";
import { AdminAccountCreateModal } from "../AdminAccountCreateView";
import {
	type AdminCombinedFormData,
	adminCombinedFormSchema,
	getAdminAccountFormValidationSchema,
	getAdminCombinedFormValidationSchema,
	getDefaultAdminAccountFormData,
	getDefaultAdminCombinedFormData,
	toAdminAccountCreateRequest,
	withResolvedPrincipal,
} from "../schema";
import { CombinedQuickSubmit } from "./CombinedQuickSubmit";
import { PrequalQuickSubmit } from "./PrequalQuickSubmit";
import { QuickSubmitHeader } from "./QuickSubmitHeader";

const defaultBondFormData: BondFormData = {
	templateId: "",
	upload: { type: "", files: [] },
};

export const AdminAccountCreateController = ({
	isCombined,
	onClose,
}: {
	isCombined: boolean;
	onClose: () => void;
}) => {
	const { submitAccount, isSubmitting } = useAdminSubmitSuretyAccount();
	const [isFullSubmissionOpen, setIsFullSubmissionOpen] = useState(false);
	const [isDuplicateCompanyOpen, setIsDuplicateCompanyOpen] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [previousAgentId, setPreviousAgentId] = useState<string>("");
	const [bondFormData, setBondFormData] = useState<BondFormData>(defaultBondFormData);

	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const isCommercialSuretyEnabled = useIsCommercialSuretyEnabled();
	const schemaRef = useRef<z.ZodType>(adminCombinedFormSchema);
	const accountSchema = useMemo(
		() =>
			getAdminAccountFormValidationSchema({
				requirePaymentAndPerformanceBonds: isCommercialSuretyEnabled,
			}),
		[isCommercialSuretyEnabled],
	);

	const formMethods = useForm<AdminCombinedFormData>({
		mode: "onBlur",
		resolver: (values, context, options) =>
			createFormResolver<AdminCombinedFormData>(schemaRef.current)(
				withResolvedPrincipal(values),
				context,
				options,
			),
		defaultValues: isCombined
			? getDefaultAdminCombinedFormData()
			: getDefaultAdminAccountFormData(),
	});
	const selectedBondType = useWatch({ control: formMethods.control, name: "bondType" });
	const combinedSchema = useMemo(
		() =>
			getAdminCombinedFormValidationSchema({
				minimalMode: isMinimalUIEnabled,
				requirePaymentAndPerformanceBonds: isCommercialSuretyEnabled,
				commercialSuretyEnabled: isCommercialSuretyEnabled,
				bondType: selectedBondType,
			}),
		[isMinimalUIEnabled, isCommercialSuretyEnabled, selectedBondType],
	);
	const schema = isCombined ? combinedSchema : accountSchema;
	schemaRef.current = schema;
	const analytics = useAccountSubmissionAnalytics<AdminCombinedFormData>(
		isCombined ? "combined" : "prequal",
		schema,
		"quick",
	);
	const { onAccountCreated, onSubmissionFailed, markEscalatedToFullForm } = analytics;

	useBondFormSync(bondFormData, formMethods, isCombined);

	const { agencyPreviews } = useAdminFetchAgencyPreviews();
	const selectedAgencyId = useWatch({ control: formMethods.control, name: "agencyId" });
	const selectedAgentId = useWatch({ control: formMethods.control, name: "agentId" });
	const followers = useWatch({ control: formMethods.control, name: "followers" });

	const { checkCompanyByName, companyChecks } = useAdminCheckCompanyByName();

	const handleCompanyNameBlur = useCallback(() => {
		const companyName = formMethods.getValues("account.details.companies.0.name");
		if (companyName) {
			checkCompanyByName(companyName, {
				onSuccess: (matches) => {
					if (matches.length > 0) {
						setIsDuplicateCompanyOpen(true);
					}
				},
			});
		}
	}, [formMethods, checkCompanyByName]);

	const { agentPreviews: agencyAgents } = useAdminFetchAgentPreviewsForAgency(
		selectedAgencyId,
		true,
	);
	const agentPreviews = agencyAgents?.filter((agent) => !agent.invitationPending);

	const defaultUnderwriterId = useMemo(() => {
		if (selectedAgencyId && agencyPreviews) {
			return agencyPreviews.find((agency) => agency.id === selectedAgencyId)?.underwriterUserId;
		}
	}, [selectedAgencyId, agencyPreviews]);

	useEffect(() => {
		if (defaultUnderwriterId) {
			formMethods.setValue("underwriterId", defaultUnderwriterId);
			if (formMethods.formState.errors.underwriterId) {
				formMethods.trigger("underwriterId");
			}
		}
	}, [defaultUnderwriterId, formMethods]);

	useEffect(() => {
		if (!selectedAgentId) return;
		if (selectedAgentId === previousAgentId) return;

		const currentFollowers = formMethods.getValues("followers") || [];
		let updatedFollowers = [...currentFollowers];

		if (previousAgentId) {
			updatedFollowers = updatedFollowers.filter((followerId) => followerId !== previousAgentId);
		}

		if (!updatedFollowers.includes(selectedAgentId)) {
			updatedFollowers.push(selectedAgentId);
		}
		formMethods.setValue("followers", updatedFollowers);

		if (formMethods.formState.errors.followers || formMethods.formState.errors.agentId) {
			formMethods.trigger(["followers", "agentId"]);
		}

		setPreviousAgentId(selectedAgentId);
	}, [selectedAgentId, previousAgentId, formMethods]);

	useRequireActiveAgentFollower(followers, agencyAgents, {
		setError: (message) => formMethods.setError("followers", { type: "manual", message }),
		clearError: () => formMethods.clearErrors("followers"),
		hasManualError: formMethods.formState.errors.followers?.type === "manual",
	});

	const onFollow = useCallback(
		(id: UserId) => {
			formMethods.setValue("followers", [...(followers || []), id]);
			if (formMethods.formState.errors.followers) {
				formMethods.trigger("followers");
			}
		},
		[followers, formMethods],
	);

	const onUnfollow = useCallback(
		(id: UserId) => {
			formMethods.setValue(
				"followers",
				(followers || []).filter((follower) => follower !== id),
			);
		},
		[followers, formMethods],
	);

	const handleSubmit = useCallback(() => {
		formMethods.handleSubmit(async (data) => {
			submitAccount({
				data: toAdminAccountCreateRequest(data),
				onSuccess: (account) => {
					onAccountCreated(account.id, data, data.account.modelSize);
					onClose();
				},
				onError: onSubmissionFailed,
			});
		})();
	}, [onAccountCreated, onSubmissionFailed, onClose, formMethods, submitAccount]);

	const handleOpenFullSubmission = useCallback(() => {
		const companies = formMethods.getValues("account.details.companies") ?? [];
		const cleaned = companies.filter((c) => c?.name);
		if (cleaned.length !== companies.length) {
			formMethods.setValue("account.details.companies", cleaned);
		}
		markEscalatedToFullForm();
		setIsFullSubmissionOpen(true);
	}, [formMethods, markEscalatedToFullForm]);

	const handleFullSubmissionClose = useCallback(() => {
		onClose();
		if (searchParams.get("section")) {
			setSearchParams("", { replace: true });
		}
	}, [onClose, searchParams, setSearchParams]);

	useEffect(() => {
		return () => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.delete("section");
					next.delete("st");
					return next;
				},
				{ replace: true },
			);
		};
	}, []);

	const header = (
		<QuickSubmitHeader
			title="New Account Submission"
			agentPreviews={agencyAgents}
			onFollow={onFollow}
			onUnfollow={onUnfollow}
		/>
	);

	if (isFullSubmissionOpen) {
		return (
			<>
				<FormSchemaProvider value={schema}>
					{isCombined ? (
						<CombinedFullSubmitWrapper
							formMethods={formMethods}
							bondFormData={bondFormData}
							setBondFormData={setBondFormData}
							onClose={handleFullSubmissionClose}
							analytics={analytics}
						/>
					) : (
						<AdminAccountCreateModal
							onClose={handleFullSubmissionClose}
							formMethods={formMethods}
							analytics={analytics}
						/>
					)}
				</FormSchemaProvider>
				{isDuplicateCompanyOpen && companyChecks && companyChecks.length > 0 && (
					<AdminDuplicateCompaniesModal
						companyChecks={companyChecks}
						onClickOutside={() => setIsDuplicateCompanyOpen(false)}
						isFullSubmission
					/>
				)}
			</>
		);
	}

	const quickSubmitProps = {
		onSubmit: handleSubmit,
		openFullSubmission: handleOpenFullSubmission,
		header,
		agentPreviews,
		isSubmitting,
		onCompanyNameBlur: handleCompanyNameBlur,
	};

	return (
		<>
			<FormProvider {...formMethods}>
				<FormSchemaProvider value={schema}>
					{isCombined ? (
						<CombinedQuickSubmit {...quickSubmitProps} />
					) : (
						<PrequalQuickSubmit {...quickSubmitProps} />
					)}
				</FormSchemaProvider>
			</FormProvider>
			{isDuplicateCompanyOpen && companyChecks && companyChecks.length > 0 && (
				<AdminDuplicateCompaniesModal
					companyChecks={companyChecks}
					onClickOutside={() => setIsDuplicateCompanyOpen(false)}
				/>
			)}
		</>
	);
};

const useBondFormSync = (
	bondFormData: BondFormData,
	formMethods: ReturnType<typeof useForm<AdminCombinedFormData>>,
	enabled = true,
) => {
	useEffect(() => {
		if (!enabled) return;
		const bondForm = transformBondFormForSubmission(bondFormData);
		formMethods.setValue("quote.bondForm", bondForm ?? null);
	}, [bondFormData, formMethods, enabled]);
};

const CombinedFullSubmitWrapper = ({
	formMethods,
	bondFormData,
	setBondFormData,
	onClose,
	analytics,
}: {
	formMethods: ReturnType<typeof useForm<AdminCombinedFormData>>;
	bondFormData: BondFormData;
	setBondFormData: (data: BondFormData) => void;
	onClose: () => void;
	analytics: AccountSubmissionAnalytics<AdminCombinedFormData>;
}) => {
	const accountNaicsCodes = useWatch({
		control: formMethods.control,
		name: "account.naicsCodes",
	});

	const formCompanies = useWatch({
		control: formMethods.control,
		name: "account.details.companies",
	});

	const companies = useMemo((): PrincipalCompanyOption[] => {
		if (!formCompanies) return [];
		return formCompanies
			.filter((c) => c?.name)
			.map((c) => ({
				name: c.name ?? "",
				isPrimary: c.isPrimaryCompany === true,
			}));
	}, [formCompanies]);

	useBondFormSync(bondFormData, formMethods);

	return (
		<BondRequestCreateContext.Provider
			value={{ accountNaicsCodes, bondFormData, setBondFormData, companies }}
		>
			<AdminAccountCreateModal
				isCombined
				onClose={onClose}
				formMethods={formMethods}
				analytics={analytics}
			/>
		</BondRequestCreateContext.Provider>
	);
};
